import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import RefreshButton from './ui/RefreshButton';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { DatePickerFr } from './ui/DatePickerFr';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import {
  FileText,
  Download,
  Mail,
  Search,
  Calendar,
  Filter as FilterIcon,
  ExternalLink,
  TrendingUp,
  Clock,
  CheckCircle2,
  RefreshCw,
  XCircle,
  Receipt,
  Wallet,
} from 'lucide-react';
import { getToken } from '../services/tokenService';
import InstitutionalHero from './ui/InstitutionalHero';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const TYPES_PLAN = [
  { value: 'all', label: 'Tous les types' },
  { value: 'classique', label: 'Abonnement Classique' },
  { value: 'usage', label: "Tarification à l'Usage" },
  { value: 'mixte', label: 'Tarification Mixte' },
  { value: 'credits', label: 'Système de Crédits' },
  { value: 'wallet', label: 'Wallet' },
  { value: 'echelonne', label: 'Paiement Échelonné' },
];

const TYPES_DOC = [
  { value: 'all', label: 'Factures + Reçus' },
  { value: 'facture', label: 'Factures uniquement' },
  { value: 'recu', label: 'Reçus uniquement' },
];

const DEFAULT_FILTERS = {
  q: '',
  type_plan: 'all',
  type_document: 'all',
  period_start: '',
  period_end: '',
};

export default function GestionFinancierePlans() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const headers = useMemo(() => ({ Authorization: `Bearer ${getToken()}` }), []);

  const fetchItems = async (filtersOverride = null) => {
    setLoading(true);
    const f = filtersOverride || filters;
    try {
      const params = new URLSearchParams();
      if (f.q) params.append('q', f.q);
      if (f.type_plan && f.type_plan !== 'all') params.append('type_plan', f.type_plan);
      if (f.type_document && f.type_document !== 'all') params.append('type_document', f.type_document);
      if (f.period_start) params.append('period_start', f.period_start);
      if (f.period_end) params.append('period_end', f.period_end);
      const res = await axios.get(`${API}/factures-plans/?${params.toString()}`, { headers });
      setItems(res.data.factures || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Erreur lors du chargement des factures');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(DEFAULT_FILTERS); /* eslint-disable-next-line */ }, []);

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    fetchItems(DEFAULT_FILTERS);
  };

  const downloadPdf = async (id, reference) => {
    try {
      const res = await axios.get(`${API}/factures-plans/${id}/pdf`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reference}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch {
      toast.error('Téléchargement impossible');
    }
  };

  const sendEmail = async (id) => {
    try {
      const res = await axios.post(`${API}/factures-plans/${id}/send-email`, {}, { headers });
      if (res.data.email_sent) {
        toast.success(`Email envoyé à ${res.data.to}`);
      } else {
        toast.info('Envoi simulé (service email non configuré)');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur envoi email');
    }
  };

  // KPI computations
  const totalEncaisse = items
    .filter((f) => f.type_document === 'recu' || f.status === 'paid')
    .reduce((s, f) => s + (Number(f.montant_ttc) || 0), 0);

  const totalEnAttente = items
    .filter((f) => f.status !== 'paid' && f.type_document !== 'recu')
    .reduce((s, f) => s + (Number(f.montant_ttc) || 0), 0);

  const nbFactures = items.filter((f) => f.type_document === 'facture').length;
  const nbRecus = items.filter((f) => f.type_document === 'recu').length;

  // Export CSV
  const handleExportCsv = () => {
    if (items.length === 0) {
      toast.error('Aucune ligne à exporter');
      return;
    }
    const escape = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v).replace(/"/g, '""');
      return /[",;\n]/.test(s) ? `"${s}"` : s;
    };
    const header = ['Reference', 'Type', 'Destinataire', 'Email', 'Plan', 'Type_plan', 'Montant_TTC', 'Emis_le', 'Statut'];
    const lines = [header.join(';')];
    items.forEach((f) => {
      lines.push([
        escape(f.reference),
        escape(f.type_document === 'recu' ? 'REÇU' : 'FACTURE'),
        escape(f.raison_sociale || `${f.prenom || ''} ${f.nom || ''}`.trim()),
        escape(f.email),
        escape(f.plan_nom),
        escape(f.type_tarification_label),
        Number(f.montant_ttc || 0).toFixed(2).replace('.', ','),
        escape(f.emission_date),
        escape(f.status === 'paid' ? 'PAYÉ' : 'EN ATTENTE'),
      ].join(';'));
    });
    const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `factures_plans_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`${items.length} ligne(s) exportée(s) au format CSV`);
  };

  const fmtAmount = (n) => Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtDate = (s) => {
    if (!s) return '—';
    try {
      const d = new Date(s);
      if (Number.isNaN(d.getTime())) return s;
      return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return s;
    }
  };

  return (
    <div className="space-y-6" data-testid="gestion-financiere-plans">
      {/* === Hero institutionnel uniforme === */}
      <InstitutionalHero
        icon={FileText}
        badge="Trésorerie & Comptabilité"
        title="Gestion Financière des Plans"
        subtitle="Émission et suivi des Factures &amp; Reçus PADH avec QR de vérification, encaissements, ventilation par plan et exports comptables."
        gradient="bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900"
        actions={
          <>
            <RefreshButton
              onRefresh={() => fetchItems()}
              loading={loading}
              successMessage="Factures actualisées"
              variant="light"
              testId="factures-refresh-btn"
            />
            <Button
              onClick={handleExportCsv}
              className="bg-yellow-400 hover:bg-yellow-500 text-slate-900 font-bold shadow-md"
              data-testid="factures-export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Exporter CSV
            </Button>
          </>
        }
      />

      {/* === KPIs === */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="kpi-rise kpi-rise-0 relative overflow-hidden border-0 shadow-lg group hover:shadow-xl transition-all">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-indigo-700" />
          <CardContent className="relative p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 rounded-lg bg-white/20 backdrop-blur">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <Badge className="bg-slate-900/70 text-white border border-white/30 text-[10px] font-bold tracking-wider px-2 py-0.5">TOTAL</Badge>
            </div>
            <div className="text-3xl font-bold text-white tabular-nums">{total}</div>
            <div className="text-xs text-blue-100 mt-1">Documents émis</div>
          </CardContent>
        </Card>

        <Card className="kpi-rise kpi-rise-1 relative overflow-hidden border-0 shadow-lg group hover:shadow-xl transition-all">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 to-teal-700" />
          <CardContent className="relative p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 rounded-lg bg-white/20 backdrop-blur">
                <Wallet className="w-5 h-5 text-white" />
              </div>
              <Badge className="bg-slate-900/70 text-white border border-white/30 text-[10px] font-bold tracking-wider px-2 py-0.5">ENCAISSÉ</Badge>
            </div>
            <div className="text-3xl font-bold text-white tabular-nums">{fmtAmount(totalEncaisse)} €</div>
            <div className="text-xs text-emerald-100 mt-1">Reçus + Factures payées</div>
          </CardContent>
        </Card>

        <Card className="kpi-rise kpi-rise-2 relative overflow-hidden border-0 shadow-lg group hover:shadow-xl transition-all">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500 to-orange-600" />
          <CardContent className="relative p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 rounded-lg bg-white/20 backdrop-blur">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <Badge className="bg-slate-900/70 text-white border border-white/30 text-[10px] font-bold tracking-wider px-2 py-0.5">EN ATTENTE</Badge>
            </div>
            <div className="text-3xl font-bold text-white tabular-nums">{fmtAmount(totalEnAttente)} €</div>
            <div className="text-xs text-amber-100 mt-1">Factures non payées</div>
          </CardContent>
        </Card>

        <Card className="kpi-rise kpi-rise-3 relative overflow-hidden border-0 shadow-lg group hover:shadow-xl transition-all">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-600 to-fuchsia-700" />
          <CardContent className="relative p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 rounded-lg bg-white/20 backdrop-blur">
                <Receipt className="w-5 h-5 text-white" />
              </div>
              <Badge className="bg-slate-900/70 text-white border border-white/30 text-[10px] font-bold tracking-wider px-2 py-0.5">VENTILATION</Badge>
            </div>
            <div className="text-3xl font-bold text-white tabular-nums">
              {nbFactures}<span className="text-base text-white/70"> · {nbRecus}</span>
            </div>
            <div className="text-xs text-purple-100 mt-1">Factures &middot; Reçus</div>
          </CardContent>
        </Card>
      </div>

      {/* === Filtres === */}
      <Card className="border border-slate-200 shadow-sm">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <FilterIcon className="w-4 h-4 text-indigo-600" /> Filtres
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={resetFilters}
              className="text-slate-500 hover:text-slate-700"
              data-testid="factures-reset-filters"
            >
              <XCircle className="w-4 h-4 mr-1" /> Réinitialiser
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
            <div className="lg:col-span-2">
              <Label className="text-xs text-slate-600">Recherche</Label>
              <div className="relative mt-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  className="pl-9"
                  placeholder="N° facture, raison sociale, nom, prénom…"
                  value={filters.q}
                  onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && fetchItems()}
                  data-testid="factures-search"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Type de plan</Label>
              <Select value={filters.type_plan} onValueChange={(v) => setFilters({ ...filters, type_plan: v })}>
                <SelectTrigger className="mt-1" data-testid="factures-type-plan"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPES_PLAN.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Type doc.</Label>
              <Select value={filters.type_document} onValueChange={(v) => setFilters({ ...filters, type_document: v })}>
                <SelectTrigger className="mt-1" data-testid="factures-type-doc"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPES_DOC.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600 flex items-center gap-1"><Calendar className="w-3 h-3" /> Du</Label>
              <DatePickerFr
                className="mt-1 w-full"
                value={filters.period_start}
                onChange={(v) => setFilters({ ...filters, period_start: v })}
                id="factures-period-start"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600 flex items-center gap-1"><Calendar className="w-3 h-3" /> Au</Label>
              <DatePickerFr
                className="mt-1 w-full"
                value={filters.period_end}
                onChange={(v) => setFilters({ ...filters, period_end: v })}
                id="factures-period-end"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button
              onClick={() => fetchItems()}
              disabled={loading}
              className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white shadow"
              data-testid="factures-search-btn"
            >
              <FilterIcon className="w-4 h-4 mr-2" /> Filtrer
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* === Table === */}
      <Card className="border border-slate-200 shadow-sm overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gradient-to-r from-slate-50 to-slate-100 border-b-2 border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3.5 text-xs font-bold text-slate-700 uppercase tracking-wider">Référence</th>
                  <th className="text-left px-4 py-3.5 text-xs font-bold text-slate-700 uppercase tracking-wider">Type</th>
                  <th className="text-left px-4 py-3.5 text-xs font-bold text-slate-700 uppercase tracking-wider">Destinataire</th>
                  <th className="text-left px-4 py-3.5 text-xs font-bold text-slate-700 uppercase tracking-wider">Plan</th>
                  <th className="text-right px-4 py-3.5 text-xs font-bold text-slate-700 uppercase tracking-wider">Montant TTC</th>
                  <th className="text-left px-4 py-3.5 text-xs font-bold text-slate-700 uppercase tracking-wider">Émis le</th>
                  <th className="text-center px-4 py-3.5 text-xs font-bold text-slate-700 uppercase tracking-wider">Statut</th>
                  <th className="text-center px-4 py-3.5 text-xs font-bold text-slate-700 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && (
                  <tr><td colSpan="8" className="py-12 text-center text-slate-400">
                    <RefreshCw className="w-5 h-5 inline animate-spin mr-2" /> Chargement…
                  </td></tr>
                )}
                {!loading && items.length === 0 && (
                  <tr>
                    <td colSpan="8" className="py-16 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="p-4 rounded-full bg-slate-100">
                          <FileText className="w-8 h-8 text-slate-400" />
                        </div>
                        <div>
                          <div className="font-semibold text-slate-700">Aucune facture trouvée</div>
                          <div className="text-sm text-slate-500 mt-1">Essayez d&apos;élargir vos filtres ou de réinitialiser</div>
                        </div>
                        <Button variant="outline" size="sm" onClick={resetFilters}>Réinitialiser les filtres</Button>
                      </div>
                    </td>
                  </tr>
                )}
                {!loading && items.map((f) => {
                  const isPaid = f.status === 'paid' || f.type_document === 'recu';
                  const isRecu = f.type_document === 'recu';
                  return (
                    <tr key={f.id} className="hover:bg-indigo-50/40 transition-colors" data-testid={`facture-row-${f.reference}`}>
                      <td className="px-4 py-3">
                        <div className="font-mono font-semibold text-indigo-700 text-xs">{f.reference}</div>
                      </td>
                      <td className="px-4 py-3">
                        {isRecu ? (
                          <Badge className="bg-emerald-700 text-white border border-emerald-900 font-semibold">
                            <Receipt className="w-3 h-3 mr-1" /> REÇU
                          </Badge>
                        ) : (
                          <Badge className="bg-blue-700 text-white border border-blue-900 font-semibold">
                            <FileText className="w-3 h-3 mr-1" /> FACTURE
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800 truncate max-w-[230px]">
                          {f.raison_sociale || `${f.prenom || ''} ${f.nom || ''}`.trim() || '—'}
                        </div>
                        <div className="text-xs text-slate-500 truncate max-w-[230px]">{f.email || '—'}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-700">{f.plan_nom || '—'}</div>
                        {f.type_tarification_label && (
                          <Badge variant="outline" className="mt-0.5 text-[10px] py-0 bg-slate-50 text-slate-600 border-slate-200 font-semibold">
                            {f.type_tarification_label}
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="font-bold text-slate-900 tabular-nums">{fmtAmount(f.montant_ttc)} €</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs text-slate-600 flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-slate-400" /> {fmtDate(f.emission_date)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {isPaid ? (
                          <Badge className="bg-emerald-700 text-white border-0 font-semibold">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> PAYÉ
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-600 text-white border-0 font-semibold">
                            <Clock className="w-3 h-3 mr-1" /> EN ATTENTE
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-0.5 rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm w-fit mx-auto">
                          <button
                            type="button"
                            title="Télécharger PDF"
                            onClick={() => downloadPdf(f.id, f.reference)}
                            className="p-1.5 rounded-md text-indigo-600 hover:bg-indigo-50 transition-colors"
                            data-testid={`facture-download-${f.reference}`}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            title="Envoyer par email"
                            onClick={() => sendEmail(f.id)}
                            disabled={!f.email}
                            className="p-1.5 rounded-md text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid={`facture-email-${f.reference}`}
                          >
                            <Mail className="w-4 h-4" />
                          </button>
                          {f.verify_url && (
                            <a
                              href={f.verify_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 rounded-md text-slate-600 hover:bg-slate-100 transition-colors"
                              title="Vérifier (QR)"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer récap */}
          {!loading && items.length > 0 && (
            <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs text-slate-600">
              <div>
                <span className="font-semibold text-slate-800">{items.length}</span> document{items.length > 1 ? 's' : ''} affiché{items.length > 1 ? 's' : ''} sur <span className="font-semibold">{total}</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1.5">
                  <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
                  Total cumulé affiché : <span className="font-bold text-slate-900">{fmtAmount(items.reduce((s, f) => s + (Number(f.montant_ttc) || 0), 0))} €</span>
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
